import { useScrollToTop } from "@/shared/hooks/useScrollToTop";
import { PageLayout } from "@/shared/ui/layouts/PageLayout";
import React from "react";

export default function PrivacyPolicyPage() {

  const scrollToTop = useScrollToTop();
  
  React.useEffect(() => {
      scrollToTop();
  }, [scrollToTop]);

  return (
    <PageLayout>
      <div className="container mx-auto">
        <div className="pt-[130px]">
          {/* Заголовок */}
          <h1 className="mb-8 border-b-4 border-black pb-4 text-3xl font-extrabold text-gray-900 md:text-4xl">
            Политика об обработке персональных данных
          </h1>

          {/* Основное содержимое */}
          <div className="space-y-6">
            {/* 1. Общие положения */}
            <section>
              <h2 className="mb-4 mt-6 text-xl font-bold text-black md:text-2xl">
                1. Общие положения
              </h2>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                1.1. Настоящая Политика об обработке персональных данных (далее
                – «Политика») принятая <strong>ООО «КРАСОТА»</strong>, ИНН
                9715494587, ОГРН 1247700647061 (далее - «Оператор») определяет
                основные принципы, цели, порядок и условия обработки
                персональных данных, меры по обеспечению безопасности и защиты
                персональных данных в соответствии с Федеральным законом от 27
                июля 2006 г. № 152-ФЗ «О персональных данных» и иными
                нормативными актами РФ.
              </p>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                1.2. Настоящая Политика является общедоступным документом и
                подлежит размещению на официальном Сайте Оператора в
                информационно-телекоммуникационной сети «Интернет» по адресу:{' '}
                <a
                  className="font-medium text-indigo-600 underline transition duration-150 hover:text-indigo-800"
                  href="https://krasota.agency"
                >
                  https://krasota.agency
                </a>
              </p>
            </section>

            {/* 2. Термины и определения */}
            <section>
              <h2 className="mb-4 mt-6 text-xl font-bold text-black md:text-2xl">
                2. Термины и определения
              </h2>
              <ul className="ml-5 list-disc space-y-3 text-gray-700">
                <li>
                  <strong>Персональные данные</strong> – любая информация,
                  относящаяся к прямо или косвенно определенному или
                  определяемому физическому лицу (субъекту персональных данных);
                </li>
                <li>
                  <strong>Обработка персональных данных</strong> – любое
                  действие (операция) или совокупность действий (операций),
                  совершаемых Оператором с использованием средств автоматизации
                  или без использования таких средств, включая сбор, запись,
                  систематизацию, накопление, хранение, уточнение (обновление,
                  изменение), извлечение, использование, передачу
                  (распространение, предоставление, доступ), обезличивание,
                  блокирование, удаление, уничтожение персональных данных;
                </li>
                <li>
                  <strong>Оператор персональных данных</strong> –
                  государственный орган, муниципальный орган, юридическое или
                  физическое лицо, самостоятельно или совместно с другими лицами
                  организующие и (или) осуществляющие обработку персональных
                  данных, а также определяющие цели обработки персональных
                  данных, состав персональных данных, подлежащих обработке,
                  действия (операции), совершаемые с персональными данными. Для
                  целей настоящей Политики под Оператором понимается
                  Индивидуальный предприниматель Третьякова Екатерина
                  Александровна;
                </li>
                <li>
                  <strong>
                    Автоматизированная обработка персональных данных
                  </strong>{' '}
                  – обработка персональных данных с помощью средств
                  вычислительной техники;
                </li>
                <li>
                  <strong>Блокирование персональных данных</strong> – временное
                  прекращение обработки персональных данных (за исключением
                  случаев, если обработка необходима для уточнения персональных
                  данных);
                </li>
                <li>
                  <strong>
                    Информационная система персональных данных (ИСПДн)
                  </strong>{' '}
                  – совокупность содержащихся в базах данных персональных данных
                  и обеспечивающих их обработку информационных технологий и
                  технических средств;
                </li>
                <li>
                  <strong>Распространение персональных данных</strong> –
                  действия, направленные на раскрытие персональных данных
                  неопределенному кругу лиц;
                </li>
                <li>
                  <strong>Трансграничная передача персональных данных</strong> –
                  передача персональных данных на территорию иностранного
                  государства органу власти иностранного государства,
                  иностранному физическому лицу или иностранному юридическому
                  лицу;
                </li>
                <li>
                  <strong>Уничтожение персональных данных</strong> – действия, в
                  результате которых становится невозможным восстановить
                  содержание персональных данных в информационной системе
                  персональных данных и (или) в результате которых уничтожаются
                  материальные носители персональных данных;
                </li>
                <li>
                  <strong>Передача персональных данных</strong> – действия,
                  направленные на сообщение персональных данных третьей стороне
                  в заранее определенных целях;
                </li>
                <li>
                  <strong>Пользователь</strong> – физическое лицо, посещающее
                  Сайт и просматривающее страницы Сайта, осуществляющее иное
                  использование Сайта;
                </li>
                <li>
                  <strong>Сбор персональных данных</strong> – действия
                  Оператора, направленные на получение персональных данных
                  непосредственно от субъектов персональных данных;
                </li>
                <li>
                  <strong>Субъект персональных данных</strong> – физическое
                  лицо, персональные данные которого обрабатываются Оператором.
                  Для целей настоящей Политики под субъектами персональных
                  данных понимаются Пользователи;
                </li>
                <li>
                  <strong>Удаление персональных данных</strong> – действия, в
                  результате которых прекращается хранение обрабатываемых
                  персональных данных на материальных носителях по достижению
                  цели их обработки или вследствие направления запроса субъекта
                  персональных данных об удалении его персональных данных;
                </li>
                <li>
                  <strong>Файлы cookie</strong> – небольшие текстовые файлы,
                  которые сохраняются на устройство, используемое субъектом
                  персональных данных для работы с Сайтом (персональный
                  компьютер, смартфон, планшетный компьютер и т.д.). Данные
                  файлы содержат сведения, которые необходимы для работы Сайта
                  (информация о настройках браузера, о просмотренных страницах,
                  о настройках интерфейса Сайта и т.п.);
                </li>
                <li>
                  <strong>Хранение персональных данных</strong> – поддержание
                  персональных данных в неизменном состоянии после их
                  сохранения, обеспечивающее возможность их последующего
                  считывания в произвольный момент времени.
                </li>
              </ul>
            </section>

            {/* 3. Принципы обработки персональных данных */}
            <section>
              <h2 className="mb-4 mt-6 text-xl font-bold text-black md:text-2xl">
                3. Принципы обработки персональных данных
              </h2>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                Обработка ПДн осуществляется на основе следующих принципов:
              </p>
              <ul className="ml-5 list-disc space-y-2 text-gray-700">
                <li>законности и справедливости;</li>
                <li>конфиденциальности;</li>
                <li>
                  своевременности и достоверности получения согласия субъекта
                  персональных данных на обработку персональных данных;
                </li>
                <li>
                  обработки только персональных данных, которые отвечают целям
                  их обработки;
                </li>
                <li>
                  соответствия содержания и объема обрабатываемых персональных
                  данных заявленным целям обработки. Обрабатываемые персональные
                  данные не должны быть избыточными по отношению к заявленным
                  целям их обработки;
                </li>
                <li>
                  недопустимости объединения баз данных, содержащих персональные
                  данные, обработка которых осуществляется в целях,
                  несовместимых между собой;
                </li>
                <li>
                  хранения персональных данных в форме, позволяющей определить
                  субъекта персональных данных, не дольше, чем этого требуют
                  цели обработки персональных данных;
                </li>
                <li>
                  уничтожения либо обезличивания персональных данных по
                  достижению целей, их обработки или в случае утраты
                  необходимости в достижении этих целей.
                </li>
              </ul>
            </section>

            {/* 4. Цели и категории данных */}
            <section>
              <h2 className="mb-4 mt-6 text-xl font-bold text-black md:text-2xl">
                4. Цели и категории данных
              </h2>

              <p className="mb-3 text-base font-semibold text-gray-800">
                4.1. Цели обработки:
              </p>
              <ul className="ml-5 list-disc space-y-2 text-gray-700">
                <li>Исполнение гражданско-правовых договоров</li>
                <li>Информирование о продуктах/услугах</li>
                <li>
                  обеспечение возможности обратной связи от Оператора по
                  запросам Пользователей;
                </li>
                <li>
                  обеспечение возможности онлайн оплаты заказанных на Сайте
                  товаров и/или услуг;
                </li>
                <li>
                  обеспечения исполнения обязательств Оператора перед
                  Пользователями;
                </li>
                <li>в целях исследовании рынка;</li>
                <li>статистических целях;</li>
                <li>
                  в иных целях, если соответствующие действия Оператора не
                  противоречат действующему законодательству, деятельности
                  Оператора, и на проведение указанной обработки получено
                  согласие Субъекта персональных данных.
                </li>
              </ul>

              <p className="mb-3 mt-5 text-base font-semibold text-gray-800">
                4.2. Категории данных:
              </p>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                Оператор осуществляет обработку следующих персональных данных
                Пользователей:
              </p>

              <div className="space-y-4 rounded-lg border bg-gray-50 p-4">
                <ul className="ml-5 list-disc space-y-2 text-gray-700">
                  <li>
                    <strong>Основные:</strong>
                    <ul className="ml-5 mt-2 list-disc space-y-1 text-gray-600">
                      <li>Фамилия, Имя, Отчество;</li>
                      <li>Адрес электронной почты;</li>
                      <li>Номер телефона;</li>
                      <li>
                        иные данные, необходимые Оператору при оказании услуг
                        Пользователям, для обеспечения функционирования Сайта.
                      </li>
                    </ul>
                  </li>
                  <li>
                    <strong>Автоматически собираемые:</strong>
                    <p className="mt-1 text-sm italic">
                      IP-адрес, файлы cookie, данные браузера (для анализа
                      посещаемости сайта).
                    </p>
                  </li>
                  <li>
                    <strong>Цель обработки персональных данных:</strong>
                    <p className="mt-1 text-sm italic">
                      В целях определения уникального посетителя сайта и
                      формирования сведений о его предпочтениях и поведении
                    </p>
                  </li>
                  <li>
                    <strong>
                      Категории субъектов, персональные данные которых подлежат
                      обработке:
                    </strong>
                    <p className="mt-1 text-sm italic">
                      Посетители Сайта Оператора
                    </p>
                  </li>
                  <li>
                    <strong>Обрабатываемые персональные данные:</strong>
                    <p className="mt-1 text-sm italic">
                      файлы cookie: файлы cookie, необходимые для обеспечения
                      корректной работы сайта; файлы cookie, регистрирующие
                      данные о поведении пользователей на сайте; файлы cookie,
                      позволяющие оптимизировать содержание сайта для
                      пользователей
                    </p>
                  </li>
                  <li>
                    <strong>Перечень действий:</strong>
                    <p className="mt-1 text-sm italic">
                      Сбор, запись, систематизация, накопление, хранение,
                      уточнение, извлечение, использование, блокирование,
                      удаление, уничтожение
                    </p>
                  </li>
                  <li>
                    <strong>Способы обработки:</strong>
                    <p className="mt-1 text-sm italic">
                      автоматизированная, с передачей по внутренней сети
                      юридического лица, с передачей по сети Интернет
                    </p>
                  </li>
                </ul>
              </div>
            </section>

            {/* 5. Способы обработки и меры безопасности */}
            <section>
              <h2 className="mb-4 mt-6 text-xl font-bold text-black md:text-2xl">
                5. Способы обработки и меры безопасности
              </h2>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                5.1. Оператор осуществляет обработку персональных данных
                субъектов персональных данных посредством совершения любого
                действия (операции) или совокупности действий (операций),
                совершаемых с использованием средств автоматизации или без
                использования таких средств.
              </p>
              <p className="mb-3 text-base font-semibold text-gray-800">
                5.2. Меры защиты:
              </p>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                Оператор принимает технические и организационно-правовые меры в
                целях обеспечения защиты персональных данных от неправомерного
                или случайного доступа к ним, уничтожения, изменения,
                блокирования, копирования, распространения, а также от иных
                неправомерных действий:
              </p>
              <ul className="ml-5 list-disc space-y-2 text-gray-700">
                <li>
                  назначено лицо, ответственное за организацию обработки
                  персональных данных;
                </li>
                <li>
                  изданы документы, определяющих политику оператора в отношении
                  обработки персональных данных, локальные акты по вопросам
                  обработки персональных данных, локальные акты, устанавливающие
                  процедуры, направленные на предотвращение и выявление
                  нарушений законодательства Российской Федерации, устранение
                  последствий таких нарушений.
                </li>
                <li>
                  обеспечено применение правовых, организационных и технических
                  мер по обеспечению безопасности персональных данных в
                  соответствии со статьей 19 Федерального закона «О персональных
                  данных»;
                </li>
                <li>
                  осуществляется внутренний контроль соответствия обработки
                  персональных данных Федеральному закону «О персональных
                  данных» и принятым в соответствии с ним нормативным правовым
                  актам, требованиям к защите персональных данных, политике
                  оператора в отношении обработки персональных данных, локальным
                  актам оператора;
                </li>
                <li>
                  работники, непосредственно осуществляющих обработку
                  персональных данных, ознакомлены с положениями
                  законодательства Российской Федерации о персональных данных, в
                  том числе требованиями к защите персональных данных,
                  документами, определяющими политику оператора в отношении
                  обработки персональных данных, локальными актами по вопросам
                  обработки персональных данных, проведено обучение указанных
                  работников.
                </li>
              </ul>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                Установлено антивирусное компьютерное программное обеспечение,
                обеспечивается конфиденциальность паролей доступа
              </p>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                При обработке персональных данных Оператор применяет правовые,
                организационные и технические меры по обеспечению безопасности
                персональных данных в соответствии со ст. 19 Федерального закона
                «О персональных данных», Постановлением Правительства РФ от
                01.11.2012 №1119 «Об утверждении требований к защите
                персональных данных при их обработке в информационных системах
                персональных данных»:
              </p>
              <ul className="ml-5 list-disc space-y-2 text-gray-700">
                <li>
                  обеспечена безопасность помещений, в которых размещена
                  информационная система;
                </li>
                <li>обеспечена сохранность носителей персональных данных;</li>
                <li>
                  утвержден перечень лиц. доступ которых к персональным данным,
                  обрабатываемым в информационной системе, необходим для
                  выполнения ими служебных (трудовых) обязанностей;
                </li>
                <li>
                  назначено должностное лицо, ответственный за обеспечение
                  безопасности персональных данных в информационной системе.
                </li>
              </ul>
            </section>

            {/* 6. Порядок и условия обработки оператором персональных данных */}
            <section>
              <h2 className="mb-4 mt-6 text-xl font-bold text-black md:text-2xl">
                6. Порядок и условия обработки оператором персональных данных
              </h2>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                6.1. Обработка персональных данных осуществляется Оператором при
                условии получения согласия субъекта персональных данных, за
                исключением случаев, установленных Законом о персональных
                данных, когда обработка персональных данных может осуществляться
                без такого согласия.
              </p>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                6.2. Согласие на обработку персональных данных дается субъектом
                персональных данных с момента начала использования сайта, в том
                числе, путем проставления отметок в графе «я даю согласие на
                обработку персональных данных», посредством совершения субъектом
                персональных данных конклюдентных действий.
              </p>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                6.3. Получение и обработка персональных данных на сайте
                используется с помощью метрических программ Яндекс.Метрика.
                Перечень обрабатываемых персональных данных посредством
                программных средств{' '}
                <a
                  className="font-medium text-indigo-600 underline transition duration-150 hover:text-indigo-800"
                  href="https://yandex.ru/support/metrica/code/data-collected.html "
                >
                  Яндекс. Метрика
                </a>
              </p>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                6.4. Обработка Оператором биометрических персональных данных
                (сведений, которые характеризуют физиологические и биологические
                особенности человека, на основании которых можно установить его
                личность) не осуществляется.
              </p>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                6.5. Оператором не осуществляется обработка специальных
                категорий персональных данных, касающихся расовой, национальной
                принадлежности, политических взглядов, религиозных или
                философских убеждений, состояния здоровья, интимной жизни, за
                исключением случаев, предусмотренных законодательством
                Российской Федерации.
              </p>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                6.6. Трансграничная передача персональных данных Оператором не
                осуществляется.
              </p>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                6.7 Передача третьим лицам:
              </p>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                Оператор может передавать персональные данные пользователей
                службе доставки и подрядчикам для выполнения обязательств по
                договору, платежным сервисам в целях организации оплаты за
                товары и/или услуги, а также бухгалтерским и иным финансовым
                организациям в целях ведения бухгалтерского учета в отношении
                гражданско-правовых договоров, ИТ и аналогичным компаниям (для
                целей информационных рассылок, хранения моих данных в базах
                данных, обеспечения работы сайта, технической поддержки сайтов,
                получения маркетинговой и статистической информации.
              </p>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                6.8. Сроки хранения, уничтожение данных, расследование утечек
              </p>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                Данные хранятся не дольше, чем требуется для целей обработки.
                Условием прекращения обработки персональных данных является
                также истечение срока действия согласия или отзыв согласия
                Субъекта персональных данных на обработку его персональных
                данных, а также выявление неправомерной обработки персональных
                данных.
              </p>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                Срок обработки персональных данных: с момента представления
                согласия оператору до даты его ликвидации, или до отзыва
                пользователем согласия на обработку его персональных данных, или
                выявления неправомерной обработки персональных данных, или в
                течение трех лет после окончания срока договора, или до
                окончания срока использования данных, установленных
                законодательством РФ.
              </p>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                Уничтожение данных осуществляется в течение 30 календарных дней
                с момента: (а) достижения цели обработки персональных данных,
                (б) отзыва согласия на обработку персональных данных или
                истечения срока действия согласия (если согласие – единственное
                законное основание обработки данных), (в) вынесения законного
                предписания (требования) уполномоченным органом, (г) истечения
                срока хранения данных, (д) выявления неправомерности дальнейшей
                обработки персональных данных.
              </p>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                Утечки данных подлежат расследованию в соответствии с
                требованиями законодательства РФ и осуществляются
                уполномоченными лицами Оператора в порядке и в сроки,
                установленным законодательством РФ.
              </p>
            </section>

            {/* 7. Права субъектов персональных данных */}
            <section>
              <h2 className="mb-4 mt-6 text-xl font-bold text-black md:text-2xl">
                7. Права субъектов персональных данных
              </h2>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                Субъекты ПДн имеют право:
              </p>
              <ul className="ml-5 list-disc space-y-2 text-gray-700">
                <li>
                  принимать решение о предоставлении своих ПДн Оператору и
                  третьим лицам и давать согласие на их обработку свободно,
                  своей волей и в своем интересе;
                </li>
                <li>отозвать свое согласие на обработку ПДн;</li>
                <li>
                  получить от Оператора информацию, касающуюся обработки их ПДн
                  посредством направления соответствующего запроса;
                </li>
                <li>
                  требовать от Оператора уточнения ПДн, их блокирования или
                  уничтожения, в случае если они являются неполными,
                  устаревшими, неточными, незаконно полученными или не являются
                  необходимыми для заявленных целей обработки;
                </li>
                <li>
                  осуществлять иные права, предусмотренные законодательством
                  Российской Федерации. Права и обязанности оператора
                  персональных данных: принимать необходимые организационные и
                  технические меры по обеспечению безопасности персональных
                  данных;
                </li>
                <li>
                  по требованию субъекта ПДн немедленно прекратить обработку его
                  ПДн в порядке, предусмотренном законодательством Российской
                  Федерации;
                </li>
                <li>
                  в случае прекращения обработки ПДн субъекта ПДн уничтожить его
                  ПДн безопасным образом и уведомить об этом субъекта ПДн в
                  срок, не превышающий 30 (тридцать) календарных дней;
                </li>
                <li>
                  предоставить субъекту ПДн возможность ознакомления с ПДн,
                  относящимися к нему, в течение 30 (тридцати) календарных дней
                  со дня получения соответствующего заявления субъекта ПДн за
                  исключением случаев, предусмотренных законодательством
                  Российской Федерации;
                </li>
                <li>
                  выполнять иные обязанности, предусмотренные законодательством
                  Российской Федерации
                </li>
              </ul>
            </section>

            {/* 8. Заключительные положения */}
            <section>
              <h2 className="mb-4 mt-6 text-xl font-bold text-black md:text-2xl">
                8. Заключительные положения
              </h2>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                В случае возникновения любых споров или разногласий, связанных с
                исполнением настоящих Правил, Субъект персональных данных и
                Оператор приложат все усилия для их разрешения путем проведения
                переговоров между ними. В случае, если споры не будут разрешены
                путем переговоров, споры подлежат разрешению в порядке,
                установленном действующим законодательством Российской
                Федерации.
              </p>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                Настоящая Политика вступает в силу для Субъекта персональных
                данных с момента начала использования Сайта Оператора и
                действует в течение неопределенного срока. Адрес для направления
                запросов Субъектов персональных данных:{' '}
                <a
                  className="font-medium text-indigo-600 underline transition duration-150 hover:text-indigo-800"
                  href="mailto:info@thekrasota.ru"
                >
                  info@thekrasota.ru
                </a>
              </p>
              <p className="mb-4 text-base leading-relaxed text-gray-700">
                Настоящая Политика может быть изменена и/или дополнена
                Оператором в любое время по своему усмотрению без необходимости
                получения на то согласия Субъекта персональных данных. Все
                изменения и/или дополнения размещаются Оператором в
                соответствующем разделе Сайта и вступают в силу в день такого
                размещения. Субъект персональных данных обязуется своевременно и
                самостоятельно знакомиться со всеми изменениями и/или
                дополнениями. При несогласии Субъекта персональных данных с
                внесенными изменениями он обязан отказаться от доступа к Сайту,
                прекратить использование материалов и сервисов Сайта.
              </p>
            </section>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
